import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Percent } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { formatPrice } from '@/lib/utils';

interface Promotion {
  refId?: string;
  name?: string;
  type?: string;
  value?: number;
  status?: string;
  badgeColor?: string;
  startDate?: string;
  endDate?: string;
  buyQuantity?: number;
  getQuantity?: number;
}

interface ProductPromotionDisplayProps {
  promotion: Promotion;
  price: number;
  currency: string;
}

export function ProductPromotionDisplay({ promotion, price, currency }: ProductPromotionDisplayProps) {
  const { t } = useTranslation();

  const renderPromotionBadge = () => {
    if (promotion.type === 'percentage') return `-${promotion.value}%`;
    if (promotion.type === 'fixed_amount') return `-${formatPrice(promotion.value || 0, currency)}`;
    if (promotion.type === 'free_shipping') return t('promotions.freeShipping');
    if (promotion.type === 'buy_x_get_y') return `${promotion.buyQuantity || 0}+${promotion.getQuantity || 0}`;
    if (promotion.type === 'bundle') return t('promotions.types.bundle');
    return null;
  };

  const PromotionContent = () => (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
      <div className="flex items-center gap-3">
        <div
          className="size-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: promotion.badgeColor ? `${promotion.badgeColor}20` : 'var(--muted)' }}
        >
          <Percent
            className="size-5"
            style={{ color: promotion.badgeColor || undefined }}
          />
        </div>
        <div>
          <p className="font-medium">{promotion.name || t('products.activePromotion')}</p>
          {promotion.type && (
            <p className="text-xs text-muted-foreground">
              {t(`promotions.types.${promotion.type}`, { defaultValue: promotion.type })}
            </p>
          )}
        </div>
      </div>
      <Badge style={{ backgroundColor: promotion.badgeColor || undefined }}>
        {renderPromotionBadge()}
      </Badge>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="size-5" />
          {t('products.activePromotion')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {promotion.refId ? (
          <Link
            to={`/promotions/${promotion.refId}`}
            className="block hover:opacity-80 transition-opacity"
          >
            <PromotionContent />
          </Link>
        ) : (
          <PromotionContent />
        )}

        {/* Percentage discount */}
        {promotion.type === 'percentage' && promotion.value && (
          <div className="p-3 rounded-lg border border-green-200 bg-green-50">
            <p className="text-sm text-muted-foreground mb-1">{t('products.customerPaysPrice')}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base text-muted-foreground line-through">
                {formatPrice(price, currency)}
              </span>
              <span className="text-xl font-bold text-green-600">
                {formatPrice(price - (price * (promotion.value / 100)), currency)}
              </span>
              <span className="text-xs text-green-600">
                (-{formatPrice(price * (promotion.value / 100), currency)})
              </span>
            </div>
          </div>
        )}

        {/* Fixed amount discount */}
        {promotion.type === 'fixed_amount' && promotion.value && (
          <div className="p-3 rounded-lg border border-green-200 bg-green-50">
            <p className="text-sm text-muted-foreground mb-1">{t('products.customerPaysPrice')}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base text-muted-foreground line-through">
                {formatPrice(price, currency)}
              </span>
              <span className="text-xl font-bold text-green-600">
                {formatPrice(Math.max(0, price - promotion.value), currency)}
              </span>
              <span className="text-xs text-green-600">
                (-{formatPrice(promotion.value, currency)})
              </span>
            </div>
          </div>
        )}

        {/* Free shipping */}
        {promotion.type === 'free_shipping' && (
          <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
            <p className="text-sm text-blue-700">{t('products.freeShippingMessage')}</p>
          </div>
        )}

        {/* Buy X Get Y */}
        {promotion.type === 'buy_x_get_y' && promotion.buyQuantity && promotion.getQuantity && (
          <div className="p-3 rounded-lg border border-green-200 bg-green-50 space-y-2">
            <p className="text-sm text-muted-foreground">{t('products.buyXGetYMessage')}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{t('products.buy')} {promotion.buyQuantity}</Badge>
              <span className="text-muted-foreground">→</span>
              <Badge className="bg-green-600">{t('products.get')} {promotion.getQuantity} {t('products.free')}</Badge>
            </div>
            <div className="pt-2 border-t border-green-200">
              <p className="text-xs text-muted-foreground mb-1">{t('products.effectivePricePerUnit')}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base text-muted-foreground line-through">
                  {formatPrice(price, currency)}
                </span>
                <span className="text-xl font-bold text-green-600">
                  {formatPrice((price * promotion.buyQuantity) / (promotion.buyQuantity + promotion.getQuantity), currency)}
                </span>
                <span className="text-xs text-muted-foreground">/ {t('products.unit')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Bundle */}
        {promotion.type === 'bundle' && (
          <div className="p-3 rounded-lg border border-purple-200 bg-purple-50">
            <p className="text-sm text-purple-700">{t('products.bundleMessage')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
